<template>
  <transition name="fade">
    <div v-if="props.show" class="select-auto">
      <div
        class="absolute w-screen h-screen bg-black opacity-70 inset-0 z-10"
        @click.stop="onModalToggle"
      />
      <div
        class="absolute inset-0 w-screen h-screen flex items-center justify-center"
      >
        <div
          class="w-screen mx-2 xl:min-w-[800px] max-w-4xl py-2 px-4 md:py-4 md:px-10 relative xl:mx-auto my-auto shadow-lg bg-white z-10"
        >
          <div>
            <div class="">
              <h2 class="text-3xl font-bold text-black text-center">
                Save Design
              </h2>
              <div class="mb-3">
                <div class="block w-full text-left">Design ID</div>
                <div class="relative w-full">
                  <input
                    class="block w-full border border-black p-2 select-text relative"
                    type="text"
                    readonly="true"
                    disabled="true"
                    placeholder="**********"
                    :value="designId"
                    :aria-label="`Design ID ${designId}`"
                  />
                  <div
                    class="absolute right-0 top-0 size-10 text-lg py-2 px-2 text-center text-gray-400"
                  >
                    <i class="fa fa-lock" />
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <div class="block w-full text-left flex items-center gap-1">
                  Design Name
                  <span class="inline-block">
                    <div class="relative group">
                      <i class="fas fa-question-circle text-gray-400 cursor-help" />
                      <div
                        class="absolute w-[200px] bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white rounded opacity-0 group-hover:opacity-100 text-[#0641ad] text-xs p-2 shadow-xl shadow-stone-300 hidden group-hover:block"
                      >
                        Give your design a unique name. The word "Custom" cannot be used in design names.
                      </div>
                    </div>
                  </span>
                </div>
                <input
                  ref="designNameInput"
                  v-model="designName"
                  class="block w-full border border-black p-2 select-text"
                  type="text"
                  placeholder="Give your design a name"
                  aria-label="Design name"
                />
                <div class="text-left text-sm mt-1">
                  <label>
                    <input
                      v-model="canFeature"
                      type="checkbox"
                      aria-label="Publish as a potential Customer Creation"
                    />
                    Submit your design for consideration to be published as a
                    potential Customer Creation and win a $50 gift certificate!
                    <span class="inline-block">
                      <div class="relative group">
                        <i class="fas fa-question-circle" />
                        <div
                          class="absolute w-[200px] bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white rounded opacity-0 group-hover:opacity-100 text-[#0641ad] text-xs p-2 shadow-xl shadow-stone-300 hidden group-hover:block"
                        >
                          Our design team reviews submissions regularly for
                          possible inclusion in Felt Right's Customer Creations.
                          Should your design be selected, we will let you know
                          by email and also send you a $50 gift certificate.
                        </div>
                      </div>
                    </span>
                  </label>
                </div>
              </div>
              <div class="mb-3">
                <div class="block w-full text-left">
                  Username
                  <span class="text-sm"
                    >Will be shown publicly as the design author</span
                  >
                </div>
                <input
                  v-model="authorName"
                  class="block w-full border border-black p-2 select-text"
                  type="text"
                  placeholder="Username"
                  aria-label="Username"
                />
              </div>
              <div class="my-4">
                <div class="mb-2">
                  <label>
                    <input
                      v-model="commercialProject"
                      type="checkbox"
                      aria-label="Commercial project"
                    />
                    Is this a Commercial Project?
                  </label>
                </div>
                <div v-if="commercialProject">
                  <div class="mb-2">
                    <div class="block w-full text-left">Project Name</div>
                    <input
                      v-model="projectName"
                      class="block w-full border border-black p-2 select-text"
                      type="text"
                      placeholder=""
                      aria-label="Project name"
                    />
                  </div>
                  <div class="mb-2">
                    <div class="block w-full text-left">
                      Felt Right Salesperson
                    </div>
                    <input
                      v-model="salesPerson"
                      class="block w-full border border-black p-2 select-text"
                      type="text"
                      placeholder=""
                      aria-label="Sales person"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="my-2">
              <button
                aria-label="Save Design"
                class="block w-full bg-black px-5 py-2 text-white hover:bg-gray-800 font-bold rounded-full"
                @click.stop="handleSaveButton"
              >
                Save Design
              </button>

              <continue-without-saving
                v-if="options?.continueWithoutSaving"
                :action="
                  () => {
                    if (options?.onContinueWithoutSaving) {
                      options.onContinueWithoutSaving();
                    }
                    onModalToggle();
                  }
                "
              />
              <button
                v-else
                aria-label="Cancel saving design"
                class="block w-full bg-white px-5 py-2 text-gray-400 font-bold mt-2 rounded-full border border-gray-300"
                @click.stop="onModalToggle"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, ref, watch } from "vue"
import { SweetAlertIcon } from '~/types/sweetalert'
import { ModalType, useModalStore } from "~/stores/modal"

const { $toast } = useNuxtApp();
const gridStore = useGridStore();
const projectStore = useProjectStore();
const loadingStore = useLoadingStore();
const userStore = useUserStore();
const designName = ref(projectStore.designName);
const designId = ref(null);
const authorName = ref(null);
const canFeature = ref(null);
const commercialProject = ref(null);
const projectName = ref(null);
const salesPerson = ref(null);
const modalStore = useModalStore();
const designNameInput = ref(null);
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["modalToggle"]);
const options = computed(() => modalStore.getModalOptions(ModalType.SaveAs));

// Watch for modal opening
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      // Modal is opening
      if (options.value?.save || options.value?.settings) {
        // Pull values from project store
        designName.value = projectStore.designName;
        designId.value = projectStore.designId;
        authorName.value = projectStore.username || userStore.email;
        canFeature.value = projectStore.canFeature;
        commercialProject.value = projectStore.isCommercialProject;
        projectName.value = projectStore.projectName;
        salesPerson.value = projectStore.salesperson;
      } else {
        // Reset all refs for saveAs
        designName.value = projectStore.designName;
        authorName.value = userStore.email; // Default to email
        canFeature.value = true;
        commercialProject.value = null;
        projectName.value = null;
        salesPerson.value = null;
        
        // Focus on design name input for Save As
        nextTick(() => {
          if (designNameInput.value) {
            designNameInput.value.focus();
          }
        });
      }
    }
  }
);

const handleSaveButton = () => {
  if (options?.value?.save || options?.value?.settings) {
    saveDesign();
  } else {
    saveAsDesign();
  }
};
const saveDesign = async () => {
  const keepLoading = options.value?.keepLoading || false;
  try {
    if (!designName.value) {
      $toast.fire({
        icon: SweetAlertIcon.INFO,
        text: "Please enter a design name",
      });
      return;
    }
    if (designName.value.toLowerCase().includes('custom')) {
      $toast.fire({
        icon: SweetAlertIcon.INFO,
        text: "Design name cannot contain the word 'Custom'",
      });
      return;
    }
    loadingStore.setLoading(true);
    projectStore.updateDetails({
      designName: designName.value,
      canFeature: canFeature.value,
      commercial: commercialProject.value,
      projectName: projectName.value,
      salesperson: salesPerson.value,
      username: authorName.value,
    });
    await projectStore.saveDesign({
      keepLoading,
    });
    modalStore.handleSuccess(ModalType.SaveAs);
  } catch (error) {
    console.error("Error saving design copy:", error);
  } finally {
    if (!keepLoading) {
      loadingStore.setLoading(false);
    }
  }
};

const saveAsDesign = async () => {
  if (!designName.value) {
    $toast.fire({
      icon: SweetAlertIcon.INFO,
      text: "Please enter a design name",
    });
    return;
  }
  if (designName.value.toLowerCase().includes('custom')) {
    $toast.fire({
      icon: SweetAlertIcon.INFO,
      text: "Design name cannot contain the word 'Custom'",
    });
    return;
  }
  try {
    loadingStore.setLoading(true);
    useRouter().replace({ query: null });
    projectStore.setDetails(
      null,
      designName,
      gridStore.gridBgColor,
      canFeature,
      commercialProject,
      projectName,
      salesPerson,
      authorName.value
    );
    userStore.name = authorName.value;
    await projectStore.save();
    onModalToggle();
  } catch (error) {
    console.error("Error saving design copy:", error);
  } finally {
    loadingStore.setLoading(false);
  }
};

const onModalToggle = () => {
  emit("modalToggle");
};
</script>

<style>
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease-out;
}
</style>
